// Shared building blocks for the session states (verifying, connection
// problem, connection banner, application error).
//
// Visual language is taken from the app shell, not invented here: colours
// come from the shared sidebar colour system (sidebarColors) and the brand
// blue #2e7cba, the card follows IdleSessionGuard's dialog (18px radius,
// 44–48px tinted icon tile, 800-weight title), the primary button is the
// login page's brand gradient, and the wordmark uses the sidebar's exact
// logo crops. Hierarchy comes from surfaces, borders, spacing and type —
// no shadows anywhere in this UI.
import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { sidebarColors } from "../../pages/(All) Sidebar/sidebarStyles";
import sessionTranslations from "./sessionTranslations";

import iconLight from "../../Images/photos/icon.png";
import iconDark from "../../Images/photos/icon121.png";
import arabicLogoLight from "../../Images/Arabic-Logo-without-BG.png";
import arabicLogoDark from "../../Images/Arabic-Logo-without-BG-Dark.png";
import englishLogoLight from "../../Images/English Nexploy V2.png";
import englishLogoDark from "../../Images/English Nexploy white V2.png";

export const BRAND = "#2e7cba";
export const BRAND_DEEP = "#084d98";

// Above the Mini Chat Dock (950) and the client badge (9998) so a full-page
// state is never covered; below IdleSessionGuard (max int), which must stay
// on top of everything.
export const SESSION_SCREEN_Z = 10000;

export const getSessionPalette = (theme) => {
  const c = sidebarColors[theme === "light" ? "light" : "dark"];
  if (theme === "light") {
    return {
      page: "#f1f1f1",
      surface: "#ffffff",
      floating: "#ffffff",
      border: c.sidebarBorder,
      text: c.text,
      textSecondary: c.textSecondary,
      muted: "#94a3b8",
      accent: BRAND,
      accentText: BRAND,
      accentSoft: c.accentSoft,
      accentBorder: c.accentBorder,
      warning: "#d97706",
      warningSoft: "#fef3c7",
      warningBorder: "rgba(217, 119, 6, 0.35)",
      danger: c.danger,
      dangerSoft: c.dangerSoft,
      secondaryBorder: "rgba(0, 0, 0, 0.12)",
      secondaryText: "#334155",
      secondaryHover: "rgba(0, 0, 0, 0.04)",
      skeleton: "rgba(0, 0, 0, 0.06)",
      skeletonShine: "rgba(0, 0, 0, 0.05)",
      chrome: c.topbarBg,
    };
  }
  return {
    page: "#0f172a",
    surface: "rgba(255, 255, 255, 0.03)",
    floating: "#1e293b",
    border: "rgba(255, 255, 255, 0.10)",
    text: c.text,
    textSecondary: c.textSecondary,
    muted: "rgba(255, 255, 255, 0.5)",
    accent: BRAND,
    accentText: "#60a5fa",
    accentSoft: c.accentSoft,
    accentBorder: c.accentBorder,
    warning: "#fbbf24",
    warningSoft: "rgba(251, 191, 36, 0.15)",
    warningBorder: "rgba(251, 191, 36, 0.35)",
    danger: c.danger,
    dangerSoft: c.dangerSoft,
    secondaryBorder: "rgba(255, 255, 255, 0.15)",
    secondaryText: "#e2e8f0",
    secondaryHover: "rgba(255, 255, 255, 0.06)",
    skeleton: "rgba(255, 255, 255, 0.06)",
    skeletonShine: "rgba(255, 255, 255, 0.05)",
    chrome: "rgba(255, 255, 255, 0.03)",
  };
};

const readStored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
};

/**
 * Theme + language for the session UI. Uses the app's Theme/Language
 * contexts; the top-level error boundary sits outside those providers, so it
 * falls back to the same persisted preferences the providers read.
 */
export const useSessionUi = () => {
  const themeCtx = useContext(ThemeContext);
  const langCtx = useContext(LanguageContext);
  const theme = themeCtx?.theme || readStored("mo3ti-theme") || "dark";
  const language = langCtx?.language || readStored("language") || "en";
  const isRTL = language === "ar";
  return {
    theme,
    language,
    isRTL,
    dir: isRTL ? "rtl" : "ltr",
    t: sessionTranslations[language] || sessionTranslations.en,
    palette: getSessionPalette(theme),
  };
};

export const formatNumber = (value, language) => {
  try {
    return new Intl.NumberFormat(language === "ar" ? "ar-EG" : "en-US").format(value);
  } catch (err) {
    return String(value);
  }
};

/** Whole seconds until `targetMs`, refreshed every second; null when unset. */
export const useSecondsUntil = (targetMs) => {
  const compute = () => (targetMs ? Math.max(0, Math.ceil((targetMs - Date.now()) / 1000)) : null);
  const [seconds, setSeconds] = useState(compute);
  useEffect(() => {
    setSeconds(compute());
    if (!targetMs) return undefined;
    const id = setInterval(() => setSeconds(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);
  return seconds;
};

/** Interactive states + motion. Class-based so :hover / :focus-visible work. */
export const SessionStyles = ({ palette }) => (
  <style>{`
    @keyframes nxSessionSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes nxSessionShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
    .nx-session-spin { animation: nxSessionSpin 1s linear infinite; }
    .nx-session-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      min-height: 44px; padding: 0 18px; border-radius: 12px;
      font-size: 14px; font-weight: 700; line-height: 1; cursor: pointer;
      transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
      white-space: nowrap;
    }
    .nx-session-btn:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }
    .nx-session-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .nx-session-btn--primary { border: none; color: #ffffff; background: linear-gradient(135deg, ${BRAND}, ${BRAND_DEEP}); }
    .nx-session-btn--primary:not(:disabled):hover { background: ${BRAND}; }
    .nx-session-btn--secondary { background: transparent; color: ${palette.secondaryText}; border: 1px solid ${palette.secondaryBorder}; }
    .nx-session-btn--secondary:not(:disabled):hover { background: ${palette.secondaryHover}; }
    .nx-session-btn--compact { min-height: 34px; padding: 0 12px; border-radius: 10px; font-size: 13px; }
    .nx-session-skeleton { position: relative; overflow: hidden; background: ${palette.skeleton}; border-radius: 10px; }
    .nx-session-skeleton::after {
      content: ""; position: absolute; inset: 0;
      background-image: linear-gradient(90deg, transparent 0%, ${palette.skeletonShine} 50%, transparent 100%);
      animation: nxSessionShimmer 2s infinite ease-in-out;
    }
    .nx-session-actions { display: flex; gap: 10px; }
    .nx-session-actions > .nx-session-btn { flex: 1; }
    @media (max-width: 420px) {
      .nx-session-actions { flex-direction: column-reverse; }
    }
    @media (prefers-reduced-motion: reduce) {
      .nx-session-spin, .nx-session-skeleton::after { animation: none; }
    }
  `}</style>
);

/** Icon + wordmark, cropped exactly like the sidebar header. */
export const NexployBrand = ({ theme, language }) => {
  const dark = theme !== "light";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }} aria-label="Nexploy">
      <div style={{ width: "1.75rem", height: "1.75rem", position: "relative", overflow: "hidden", flexShrink: 0 }}>
        <img
          src={dark ? iconDark : iconLight}
          alt=""
          style={{
            position: "absolute", width: "170%", height: "170%", maxWidth: "none",
            left: "50%", top: "50%", transform: "translate(-52.8%, -50.3%)",
          }}
        />
      </div>
      {language === "ar" ? (
        <span style={{ position: "relative", display: "block", width: 84, height: 32, overflow: "hidden", flexShrink: 0 }}>
          <img
            src={dark ? arabicLogoDark : arabicLogoLight}
            alt=""
            style={{ position: "absolute", width: 116, height: 116, maxWidth: "none", left: -16.3, top: -42.9 }}
          />
        </span>
      ) : (
        <span style={{ position: "relative", display: "block", width: 108, height: 26, overflow: "hidden", flexShrink: 0 }}>
          <img
            src={dark ? englishLogoDark : englishLogoLight}
            alt=""
            style={{ position: "absolute", height: 61, width: 168.5, maxWidth: "none", left: -38.5, top: -15.3 }}
          />
        </span>
      )}
    </div>
  );
};

/** 48px rounded tile holding a status icon, tinted by tone. */
export const IconTile = ({ icon: Icon, tone = "accent", palette, size = 48 }) => {
  const tones = {
    accent: { bg: palette.accentSoft, fg: palette.accentText },
    warning: { bg: palette.warningSoft, fg: palette.warning },
    danger: { bg: palette.dangerSoft, fg: palette.danger },
  };
  const { bg, fg } = tones[tone] || tones.accent;
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: 14, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: bg, color: fg,
      }}
    >
      <Icon size={Math.round(size * 0.5)} strokeWidth={2} />
    </div>
  );
};

/** Full-page frame: brand header + centred content, above floating widgets. */
export const SessionScreenFrame = ({ ui, children, label }) => (
  <div
    dir={ui.dir}
    lang={ui.language}
    role="main"
    aria-label={label}
    style={{
      position: "fixed", inset: 0, zIndex: SESSION_SCREEN_Z,
      display: "flex", flexDirection: "column",
      background: ui.palette.page, color: ui.palette.text,
      overflowY: "auto",
    }}
  >
    <SessionStyles palette={ui.palette} />
    <header style={{ padding: "18px 20px", display: "flex", alignItems: "center" }}>
      <NexployBrand theme={ui.theme} language={ui.language} />
    </header>
    <div
      style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "8px 16px 48px",
      }}
    >
      {children}
    </div>
  </div>
);

/** The status card used by the connection-problem and error screens. */
export const SessionCard = ({ ui, children }) => (
  <section
    style={{
      width: "100%", maxWidth: 460,
      background: ui.palette.surface,
      border: `1px solid ${ui.palette.border}`,
      borderRadius: 18,
      padding: "clamp(22px, 5vw, 32px)",
      textAlign: "start",
    }}
  >
    {children}
  </section>
);

export const CardTitle = ({ ui, children, id }) => (
  <h1 id={id} style={{ margin: "18px 0 8px", fontSize: 20, fontWeight: 800, lineHeight: 1.35, color: ui.palette.text }}>
    {children}
  </h1>
);

export const CardBody = ({ ui, children }) => (
  <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: ui.palette.textSecondary }}>{children}</p>
);

export const ReferenceLine = ({ ui, label, value }) =>
  value ? (
    <p style={{ margin: "18px 0 0", fontSize: 12, color: ui.palette.muted, textAlign: "center" }}>
      {label}: <span dir="ltr" style={{ fontWeight: 600, letterSpacing: "0.02em" }}>{value}</span>
    </p>
  ) : null;
